import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { Tabs, type Tab } from "@/components/ui/tabs/Tabs";
import type { WorkerTool } from "@/services/api/worker-ani/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashAlt,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import SelectDropdown from "@/components/ui/select-dropdown";
import DynamicListInput from "@/components/ui/dynamic-list-input";
import FormInput from "@/components/ui/form-input";
import TextArea from "@/components/ui/form-input/TextArea";

interface CapabilityEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  capability: WorkerTool;
  onSave: (capability: WorkerTool) => void;
}

// Memoized Details Tab Component
const DetailsTab = memo(
  ({
    formData,
    handleInputChange,
    typeOptions,
  }: {
    formData: WorkerTool;
    handleInputChange: (field: keyof WorkerTool, value: any) => void;
    typeOptions: Array<{ value: string; label: string }>;
  }) => {
    return (
      <div className="px-1.5 space-y-6">
        <FormInput
          label="Name"
          value={formData.display_name}
          onChange={(e) => handleInputChange("display_name", e.target.value)}
        />
        <SelectDropdown
          label="Type"
          options={typeOptions}
          value={formData.tool_type || "basic"}
          onChange={(value) => handleInputChange("tool_type", value as string)}
        />
        <TextArea
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          rows={3}
        />
        <DynamicListInput
          label="Rules"
          items={formData.rules || []}
          onChange={(newRules) => handleInputChange("rules", newRules)}
          placeholder="Enter a rule"
          addButtonLabel="Add Rule"
        />
      </div>
    );
  }
);

// Memoized Fields Tab Component
const FieldsTab = memo(
  ({
    formData,
    handleInputChange,
  }: {
    formData: WorkerTool;
    handleInputChange: (field: keyof WorkerTool, value: any) => void;
  }) => {
    const fields = formData.custom_config?.fields || [];

    const handleFieldChange = useCallback(
      (index: number, field: "name" | "description", value: string) => {
        const newFields = [...fields];
        newFields[index] = { ...newFields[index], [field]: value };
        handleInputChange("custom_config", {
          ...formData.custom_config,
          fields: newFields,
        });
      },
      [fields, formData.custom_config, handleInputChange]
    );

    const addField = useCallback(() => {
      const newFields = [
        ...fields,
        { name: "", description: "", type: "text", required: false },
      ];
      handleInputChange("custom_config", {
        ...formData.custom_config,
        fields: newFields,
      });
    }, [fields, formData.custom_config, handleInputChange]);

    const removeField = useCallback(
      (index: number) => {
        const newFields = fields.filter((_, i: number) => i !== index);
        handleInputChange("custom_config", {
          ...formData.custom_config,
          fields: newFields,
        });
      },
      [fields, formData.custom_config, handleInputChange]
    );

    return (
      <div className="p-4">
        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-4">
          Fields
        </h3>
        <div className="space-y-3">
          {fields.map(
            (field: { name: string; description: string }, index: number) => (
              <div key={index} className="flex items-center gap-3">
                <FormInput
                  placeholder="Field (e.g. policy_number)"
                  value={field.name}
                  onChange={(e) =>
                    handleFieldChange(index, "name", e.target.value)
                  }
                />
                <FormInput
                  placeholder="Description"
                  value={field.description}
                  onChange={(e) =>
                    handleFieldChange(index, "description", e.target.value)
                  }
                />
                <div
                  onClick={() => removeField(index)}
                  className="flex self-stretch items-center flex-none h-9 rounded-xl aspect-square justify-center hover:bg-red-400/10 hover:text-red-400"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </div>
              </div>
            )
          )}
        </div>
        <Button outline onClick={addField} className="mt-4">
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Field
        </Button>
      </div>
    );
  }
);

export default function CapabilityEditDialog({
  isOpen,
  onClose,
  capability,
  onSave,
}: CapabilityEditDialogProps) {
  const [formData, setFormData] = useState<WorkerTool>(capability);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    setFormData(capability);
  }, [capability]);

  const handleInputChange = useCallback(
    (field: keyof WorkerTool, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSave = useCallback(() => {
    onSave(formData);
  }, [formData, onSave]);

  const typeOptions = useMemo(
    () => [
      { value: "general", label: "General" },
      { value: "data_extraction", label: "Data Extraction" },
    ],
    []
  );

  const tabs: Tab[] = useMemo(() => {
    const tabList: Tab[] = [
      {
        id: "details",
        label: "Details",
        content: (
          <DetailsTab
            formData={formData}
            handleInputChange={handleInputChange}
            typeOptions={typeOptions}
          />
        ),
      },
    ];

    if (formData.tool_type === "data_extraction") {
      tabList.push({
        id: "fields",
        label: "Fields",
        content: (
          <FieldsTab
            formData={formData}
            handleInputChange={handleInputChange}
          />
        ),
      });
    }

    return tabList;
  }, [formData, handleInputChange, typeOptions]);

  return (
    <Dialog open={isOpen} onClose={onClose} size="3xl">
      <DialogTitle>{capability.display_name}</DialogTitle>
      <DialogBody>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          disableIconEdit={true}
          onChange={(tabId) => setActiveTab(tabId)}
        />
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="indigo" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
